/**
 * Vista "Datos Generales" — combina ventas de TODAS las hojas de venta con
 * los costos (ADS + gastos generales) para mostrar utilidad y métricas
 * derivadas. Filtro propio de Desde/Hasta que aplica a ambas fuentes.
 */

import { Chart } from 'chart.js/auto';
import { $ } from '../utils/dom.js';
import { state } from '../state.js';
import { fmtMoney, fmtInt, escapeHtml } from '../utils/format.js';
import { montoDisplay, convertFromMXN } from '../utils/currency.js';
import { bucketKey } from '../utils/date.js';
import { fetchCostsRows } from '../api/costsSheet.js';
import { fetchAllSalesRows } from '../api/sheet.js';
import { renderPulso, renderProductos, buildProductInsights } from './productos.js';

// Estado local de esta vista — completamente independiente del tab Ventas.
const localState = {
  salesRows: [],          // [{FECHA, MONTO, ...}] combinadas de todas las hojas
  costoAds: [],
  gastosGenerales: [],
  filters: { desde: '', hasta: '' },
  isLoadingCosts: false,
  isLoadingSales: false,
  loadedOnce: false,
};

export function bindGeneralView() {

  $('gDesde').oninput = (e) => { localState.filters.desde = e.target.value; renderGeneralView(); };
  $('gHasta').oninput = (e) => { localState.filters.hasta = e.target.value; renderGeneralView(); };
  $('gClearBtn').onclick = () => {
    localState.filters = { desde: '', hasta: '' };
    $('gDesde').value = '';
    $('gHasta').value = '';
    renderGeneralView();
  };
}

/**
 * Punto de entrada de la vista: carga ventas agregadas + costos en paralelo,
 * después renderiza. Llamado al activar el tab y por el botón Refrescar.
 */
export async function activateGeneralView({ force = false } = {}) {
  const needsInitial = !localState.loadedOnce;
  const tasks = [];

  if (needsInitial || force) {
    tasks.push(loadSales());
    tasks.push(loadCosts());
  }

  await Promise.all(tasks);
  if (needsInitial) localState.loadedOnce = true;
  renderGeneralView();
}

async function loadSales() {
  if (localState.isLoadingSales) return;
  localState.isLoadingSales = true;
  try {
    localState.salesRows = await fetchAllSalesRows();
  } catch (e) {
    console.error('Error cargando ventas agregadas:', e);
  } finally {
    localState.isLoadingSales = false;
  }
}

async function loadCosts() {
  if (localState.isLoadingCosts) return;
  if (!state.costsSheet) {
    // No hay hoja de costos detectada — render con datos vacíos.
    return;
  }
  localState.isLoadingCosts = true;
  try {
    const { costoAds, gastosGenerales } = await fetchCostsRows(state.costsSheet);
    localState.costoAds = costoAds;
    localState.gastosGenerales = gastosGenerales;
    localState.loadedOnce = true;
  } catch (e) {
    console.error('Error cargando COSTOS/GASTOS:', e);
  } finally {
    localState.isLoadingCosts = false;
  }
}

// ─── Render ───────────────────────────────────────────────────────────────

export function renderGeneralView() {
  const { desde, hasta } = localState.filters;
  const dDesde = desde ? new Date(desde + 'T00:00:00') : null;
  const dHasta = hasta ? new Date(hasta + 'T23:59:59') : null;
  const inRange = (d) => {
    if (!d) return !dDesde && !dHasta; // sin fecha solo cuenta si no hay filtro
    if (dDesde && d < dDesde) return false;
    if (dHasta && d > dHasta) return false;
    return true;
  };

  // Ventas: filtra las filas agregadas que cargamos en localState.salesRows.
  const ventasFiltered = localState.salesRows.filter((r) => inRange(r.FECHA));
  const ventasMonto = ventasFiltered.reduce((s, r) => s + montoDisplay(r), 0);
  const ventasConMonto = ventasFiltered.filter((r) => r.MONTO > 0);
  const numVentas = ventasFiltered.length;

  // Costos (nativos en MXN) → convertidos a la moneda de display.
  const adsFiltered = localState.costoAds.filter((e) => inRange(e.fecha));
  const ggFiltered = localState.gastosGenerales.filter((e) => inRange(e.fecha));
  const costoAdsTotal = adsFiltered.reduce((s, e) => s + convertFromMXN(e.total), 0);
  const gastosTotal = ggFiltered.reduce((s, e) => s + convertFromMXN(e.total), 0);

  const utilidad = ventasMonto - costoAdsTotal - gastosTotal;
  const margenPct = ventasMonto > 0 ? (utilidad / ventasMonto) * 100 : null;
  const roas = costoAdsTotal > 0 ? (ventasMonto / costoAdsTotal) : null;
  const costoPorVenta = numVentas > 0 ? (costoAdsTotal + gastosTotal) / numVentas : null;
  const ticketProm = ventasConMonto.length > 0 ? ventasMonto / ventasConMonto.length : 0;

  // ── KPIs ─────────────────────────────────────────────────────────────
  $('gKpiVentas').textContent = fmtMoney(ventasMonto);
  $('gKpiVentasSub').textContent = `${fmtInt(numVentas)} ${numVentas === 1 ? 'venta' : 'ventas'}`;

  $('gKpiCostoAds').textContent = fmtMoney(costoAdsTotal);
  $('gKpiCostoAdsSub').textContent = `${fmtInt(adsFiltered.length)} ${adsFiltered.length === 1 ? 'cargo' : 'cargos'}`;

  $('gKpiGastos').textContent = fmtMoney(gastosTotal);
  $('gKpiGastosSub').textContent = `${fmtInt(ggFiltered.length)} ${ggFiltered.length === 1 ? 'cargo' : 'cargos'}`;

  const kpiUtil = $('gKpiUtilidad');
  kpiUtil.textContent = fmtMoney(utilidad);
  kpiUtil.classList.toggle('positive', utilidad > 0);
  kpiUtil.classList.toggle('negative', utilidad < 0);
  $('gKpiUtilidadSub').textContent = margenPct != null
    ? `Margen ${margenPct.toFixed(1)}%`
    : 'Sin ventas';

  $('gKpiRoas').textContent = roas != null ? `${roas.toFixed(2)}×` : '—';
  $('gKpiRoasSub').textContent = roas != null
    ? `por cada $1 en ads`
    : 'sin gasto en ads';

  $('gKpiCpv').textContent = costoPorVenta != null ? fmtMoney(costoPorVenta) : '—';
  $('gKpiCpvSub').textContent = costoPorVenta != null
    ? `costo total ÷ ventas`
    : 'sin ventas';

  $('gKpiTicket').textContent = fmtMoney(ticketProm);
  $('gKpiTicketSub').textContent = `por venta con monto`;

  // ── Pulso y análisis por producto ────────────────────────────────────
  renderPulso(localState.salesRows);
  renderProductos({ allRows: localState.salesRows, ventasFiltered });

  // ── Tablas de desglose ───────────────────────────────────────────────
  renderDesglose('gCostoAdsTable', adsFiltered, 'Sin cargos de ads en este rango.');
  renderDesglose('gGastosTable', ggFiltered, 'Sin gastos generales en este rango.');

  // ── Análisis financiero ──────────────────────────────────────────────
  renderFinancialAnalysis({
    ventasFiltered, adsFiltered, ggFiltered,
    ventasMonto, costoAdsTotal, gastosTotal,
    utilidad, margenPct, roas, ticketProm,
  });
}

function renderDesglose(tableId, entries, emptyMsg) {
  const t = $(tableId);
  if (!t) return;
  if (!entries.length) {
    t.innerHTML = `<tbody><tr><td class="empty" colspan="3">${emptyMsg}</td></tr></tbody>`;
    return;
  }
  const total = entries.reduce((s, e) => s + convertFromMXN(e.total), 0);
  const sorted = [...entries].sort((a, b) => {
    const ta = a.fecha ? a.fecha.getTime() : 0;
    const tb = b.fecha ? b.fecha.getTime() : 0;
    return tb - ta; // más reciente primero
  });
  t.innerHTML = `
    <thead>
      <tr>
        <th>Fecha</th>
        <th>Descripción</th>
        <th class="num">Total</th>
      </tr>
    </thead>
    <tbody>
      ${sorted.map((e) => `
        <tr>
          <td>${e.fecha ? e.fecha.toLocaleDateString('es-MX', { dateStyle: 'short' }) : '—'}</td>
          <td>${escapeHtml(e.descripcion) || '<span class="meta">—</span>'}</td>
          <td class="num">${fmtMoney(convertFromMXN(e.total))}</td>
        </tr>
      `).join('')}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="2"><strong>Total</strong></td>
        <td class="num"><strong>${fmtMoney(total)}</strong></td>
      </tr>
    </tfoot>`;
}

// ─── Análisis financiero ────────────────────────────────────────────────

let finChart = null;

const fmtPct = (n) => (n == null || !isFinite(n)) ? '—' : `${n.toFixed(1)}%`;

// 'YYYY-MM' → "may 2026"
function monthLabel(key) {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' });
}

/** Agrupa ventas + costos por mes. Omite registros sin fecha. */
function buildMonthly(ventasFiltered, adsFiltered, ggFiltered) {
  const map = new Map();
  const add = (d, field, val) => {
    const k = bucketKey(d, 'month');
    if (k === '—') return;
    if (!map.has(k)) map.set(k, { ventas: 0, ads: 0, gastos: 0 });
    map.get(k)[field] += val || 0;
  };
  ventasFiltered.forEach((r) => add(r.FECHA, 'ventas', montoDisplay(r)));
  adsFiltered.forEach((e) => add(e.fecha, 'ads', convertFromMXN(e.total)));
  ggFiltered.forEach((e) => add(e.fecha, 'gastos', convertFromMXN(e.total)));
  return [...map.keys()].sort().map((k) => {
    const v = map.get(k);
    const costos = v.ads + v.gastos;
    const utilidad = v.ventas - costos;
    return {
      mes: k,
      label: monthLabel(k),
      ventas: v.ventas,
      ads: v.ads,
      gastos: v.gastos,
      costos,
      utilidad,
      margen: v.ventas > 0 ? (utilidad / v.ventas) * 100 : null,
    };
  });
}

function renderFinancialAnalysis(d) {
  const { ventasMonto, costoAdsTotal, gastosTotal, margenPct, ticketProm } = d;

  // KPIs extra
  const adsPct = ventasMonto > 0 ? (costoAdsTotal / ventasMonto) * 100 : null;
  const gastosPct = ventasMonto > 0 ? (gastosTotal / ventasMonto) * 100 : null;
  const costosTotales = costoAdsTotal + gastosTotal;

  const elMargen = $('gFinMargen');
  elMargen.textContent = fmtPct(margenPct);
  elMargen.classList.toggle('positive', margenPct != null && margenPct > 0);
  elMargen.classList.toggle('negative', margenPct != null && margenPct < 0);
  $('gFinMargenSub').textContent = margenPct == null
    ? 'Sin ventas'
    : 'utilidad ÷ ventas';

  $('gFinAdsPct').textContent = fmtPct(adsPct);
  $('gFinAdsPctSub').textContent = adsPct == null ? 'sin ventas' : 'de cada venta va a ads';

  $('gFinGastosPct').textContent = fmtPct(gastosPct);
  $('gFinGastosPctSub').textContent = gastosPct == null ? 'sin ventas' : 'de cada venta a gastos';

  $('gFinBreakeven').textContent = costosTotales > 0 ? fmtMoney(costosTotales) : '—';
  $('gFinBreakevenSub').textContent = (costosTotales > 0 && ticketProm > 0)
    ? `≈ ${fmtInt(Math.ceil(costosTotales / ticketProm))} ventas al ticket prom.`
    : 'ventas para cubrir costos';

  const monthly = buildMonthly(d.ventasFiltered, d.adsFiltered, d.ggFiltered);
  renderFinChart(monthly);
  renderInsights(d, monthly);
  renderMonthsTable(monthly);
}

/**
 * Una barra apilada por mes: costos (naranja) + utilidad (verde) = ventas.
 * La utilidad negativa se dibuja en rojo bajo cero (la pérdida se VE).
 * El margen % va como etiqueta encima de cada barra — sin segundo eje.
 */
function renderFinChart(monthly) {
  const canvas = $('gFinChart');
  if (!canvas) return;
  if (finChart) { finChart.destroy(); finChart = null; }
  if (!monthly.length) return;

  // Etiqueta de margen % sobre cada barra (plugin inline, sin dependencias).
  const margenLabels = {
    id: 'margenLabels',
    afterDatasetsDraw(chart) {
      const { ctx, scales: { x, y } } = chart;
      ctx.save();
      ctx.font = '600 11px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      monthly.forEach((m, i) => {
        if (m.margen == null || !isFinite(m.margen)) return;
        const top = Math.max(m.ventas, m.costos, 0); // tope visual del stack
        ctx.fillStyle = m.margen >= 0 ? '#86efac' : '#fca5a5';
        ctx.fillText(`${m.margen.toFixed(0)}%`, x.getPixelForValue(i), y.getPixelForValue(top) - 4);
      });
      ctx.restore();
    },
  };

  finChart = new Chart(canvas.getContext('2d'), {
    type: 'bar',
    data: {
      labels: monthly.map((m) => m.label),
      datasets: [
        {
          label: 'Costos',
          data: monthly.map((m) => m.costos),
          backgroundColor: 'rgba(245,158,11,.55)',
          borderColor: '#f59e0b',
          borderWidth: 1,
          borderRadius: 4,
          maxBarThickness: 56,
        },
        {
          label: 'Utilidad',
          data: monthly.map((m) => m.utilidad),
          backgroundColor: (c) => (c.raw >= 0 ? 'rgba(34,197,94,.55)' : 'rgba(239,68,68,.55)'),
          borderColor: (c) => (c.raw >= 0 ? '#22c55e' : '#ef4444'),
          borderWidth: 1,
          borderRadius: 4,
          maxBarThickness: 56,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 18 } }, // aire para las etiquetas de margen
      plugins: {
        legend: { labels: { color: '#ffffff' } },
        tooltip: {
          callbacks: {
            label: (c) => `${c.dataset.label}: ${fmtMoney(c.parsed.y)}`,
            footer: (items) => {
              const m = monthly[items[0]?.dataIndex];
              if (!m) return '';
              return [`Ventas: ${fmtMoney(m.ventas)}`, `Margen: ${fmtPct(m.margen)}`];
            },
          },
        },
      },
      scales: {
        x: { stacked: true, ticks: { color: '#a1a1a1' }, grid: { color: 'rgba(255,255,255,.05)' } },
        y: { stacked: true, ticks: { color: '#a1a1a1', callback: (v) => '$' + v }, grid: { color: 'rgba(255,255,255,.05)' } },
      },
    },
    plugins: [margenLabels],
  });
}

function renderInsights(d, monthly) {
  const { ventasMonto, costoAdsTotal, gastosTotal, utilidad, margenPct, roas } = d;
  const ul = $('gFinInsights');
  if (!ul) return;
  const items = [];

  if (ventasMonto <= 0) {
    items.push({ tone: 'warn', text: 'No hay ventas en el rango seleccionado para analizar.' });
  } else {
    // Utilidad / margen
    if (utilidad < 0) {
      items.push({ tone: 'bad', text: `Estás operando con pérdida: ${fmtMoney(utilidad)} (margen ${margenPct.toFixed(1)}%). Los costos superan a las ventas.` });
    } else if (margenPct < 15) {
      items.push({ tone: 'warn', text: `Margen ajustado (${margenPct.toFixed(1)}%). Hay poco colchón ante imprevistos.` });
    } else if (margenPct < 30) {
      items.push({ tone: 'good', text: `Margen saludable (${margenPct.toFixed(1)}%).` });
    } else {
      items.push({ tone: 'good', text: `Margen excelente (${margenPct.toFixed(1)}%).` });
    }

    // Peso de ADS
    const adsPct = (costoAdsTotal / ventasMonto) * 100;
    if (adsPct >= 50) {
      items.push({ tone: 'bad', text: `La publicidad consume el ${adsPct.toFixed(1)}% de tus ventas — es muy alto.` });
    } else if (adsPct >= 30) {
      items.push({ tone: 'warn', text: `La publicidad consume el ${adsPct.toFixed(1)}% de tus ventas.` });
    } else if (costoAdsTotal > 0) {
      items.push({ tone: 'good', text: `Gasto en publicidad controlado: ${adsPct.toFixed(1)}% de las ventas.` });
    }

    // ROAS
    if (roas != null) {
      const tone = roas >= 3 ? 'good' : roas >= 1.5 ? 'warn' : 'bad';
      items.push({ tone, text: `ROAS de ${roas.toFixed(2)}×: por cada $1 en ads generas ${fmtMoney(roas)} en ventas.` });
    }

    // Peso de gastos generales
    const gastosPct = (gastosTotal / ventasMonto) * 100;
    if (gastosTotal > 0 && gastosPct >= 30) {
      items.push({ tone: 'warn', text: `Los gastos generales representan el ${gastosPct.toFixed(1)}% de las ventas.` });
    }
  }

  // Tendencia (último mes vs anterior)
  if (monthly.length >= 2) {
    const last = monthly[monthly.length - 1];
    const prev = monthly[monthly.length - 2];
    const diff = last.utilidad - prev.utilidad;
    if (Math.abs(diff) > 0.005) {
      const pct = prev.utilidad !== 0 ? (diff / Math.abs(prev.utilidad)) * 100 : null;
      const dir = diff > 0 ? 'subió' : 'bajó';
      const pctTxt = pct != null ? ` (${diff > 0 ? '+' : ''}${pct.toFixed(1)}%)` : '';
      items.push({
        tone: diff > 0 ? 'good' : 'warn',
        text: `La utilidad ${dir} de ${fmtMoney(prev.utilidad)} en ${prev.label} a ${fmtMoney(last.utilidad)} en ${last.label}${pctTxt}.`,
      });
    }
  }

  // Alertas por producto (rachas sin ventas, caídas vs su promedio, etc.)
  items.push(...buildProductInsights({ allRows: localState.salesRows, ventasFiltered: d.ventasFiltered }));

  if (!items.length) {
    ul.innerHTML = `<li class="ins-info">Sin observaciones para este rango.</li>`;
    return;
  }
  ul.innerHTML = items.map((i) => `<li class="ins-${i.tone}">${escapeHtml(i.text)}</li>`).join('');
}

function renderMonthsTable(monthly) {
  const t = $('gFinMesesTable');
  const best = $('gFinMejorMes');
  const worst = $('gFinPeorMes');
  if (!t) return;

  if (!monthly.length) {
    t.innerHTML = `<tbody><tr><td class="empty" colspan="6">Sin datos con fecha en este rango.</td></tr></tbody>`;
    if (best) best.textContent = '—';
    if (worst) worst.textContent = '—';
    return;
  }

  const sortedByUtil = [...monthly].sort((a, b) => b.utilidad - a.utilidad);
  const mejor = sortedByUtil[0];
  const peor = sortedByUtil[sortedByUtil.length - 1];
  if (best) best.textContent = `${mejor.label} · ${fmtMoney(mejor.utilidad)}`;
  if (worst) worst.textContent = `${peor.label} · ${fmtMoney(peor.utilidad)}`;

  const delta = (curr, prev) => {
    if (prev == null) return '<span class="meta">—</span>';
    const diff = curr - prev;
    if (Math.abs(diff) < 0.005) return '<span class="meta">=</span>';
    const up = diff > 0;
    const pct = prev !== 0 ? ` ${up ? '+' : ''}${((diff / Math.abs(prev)) * 100).toFixed(0)}%` : '';
    return `<span class="${up ? 'delta-up' : 'delta-down'}">${up ? '▲' : '▼'}${pct}</span>`;
  };

  t.innerHTML = `
    <thead>
      <tr>
        <th>Mes</th>
        <th class="num">Ventas</th>
        <th class="num">Costos</th>
        <th class="num">Utilidad</th>
        <th class="num">Margen</th>
        <th class="num">Δ Utilidad</th>
      </tr>
    </thead>
    <tbody>
      ${monthly.map((m, i) => {
        const prev = i > 0 ? monthly[i - 1] : null;
        return `
        <tr>
          <td>${escapeHtml(m.label)}</td>
          <td class="num">${fmtMoney(m.ventas)}</td>
          <td class="num">${fmtMoney(m.costos)}</td>
          <td class="num ${m.utilidad < 0 ? 'negative' : m.utilidad > 0 ? 'positive' : ''}">${fmtMoney(m.utilidad)}</td>
          <td class="num">${fmtPct(m.margen)}</td>
          <td class="num">${delta(m.utilidad, prev ? prev.utilidad : null)}</td>
        </tr>`;
      }).join('')}
    </tbody>`;
}

/**
 * Helper para que main.js pueda forzar el refetch de TODO (ventas + costos)
 * cuando el usuario aprieta Refrescar estando en la vista general.
 */
export async function refreshGeneralView() {
  await Promise.all([loadSales(), loadCosts()]);
  renderGeneralView();
}
